import {
  createFileRoute,
  Link,
  redirect,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { MessageSquare, Loader2, Check } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { ResetPasswordRequestSchema, authApi } from "@/lib/api";

export const Route = createFileRoute("/auth/reset-password")({
  beforeLoad: () => {
    const token = localStorage.getItem("auth_token");
    if (token) {
      throw redirect({ to: "/dashboard" });
    }
  },
  validateSearch: (search: Record<string, unknown>) => {
    return {
      token: search.token as string,
    };
  },
  component: ResetPassword,
});

function ResetPassword() {
  const navigate = useNavigate();
  const { token } = useSearch({ from: "/auth/reset-password" });

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  // Create reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: authApi.resetPassword,
    onSuccess: () => {
      setIsSuccess(true);
    },
    onError: (error: Error) => {
      setError(error.message || "An error occurred. Please try again.");
      console.error("Reset password error:", error);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Check if token exists
    if (!token) {
      setError(
        "Missing password reset token. Please request a new password reset link.",
      );
      return;
    }

    // Check if passwords match
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    try {
      // Validate form data with Zod schema
      const resetPasswordData = ResetPasswordRequestSchema.parse({
        token,
        password,
      });

      // Execute reset password mutation
      resetPasswordMutation.mutate(resetPasswordData);
    } catch (err: any) {
      // Handle validation errors
      if (err.errors) {
        setError(err.errors.map((e: any) => e.message).join(", "));
      } else {
        setError("Please check your input and try again.");
      }
    }
  };

  const handleLogin = () => {
    navigate({
      to: "/auth/login",
      search: { redirectTo: "/auth/reset-password" },
    });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4 relative max-w-lg mx-auto">
      {/* Background elements */}
      <div className="absolute top-[20%] right-[10%] w-64 h-64 bg-blue-300 rounded-full blur-3xl opacity-20 animate-float"></div>
      <div
        className="absolute bottom-[10%] left-[15%] w-72 h-72 bg-purple-300 rounded-full blur-3xl opacity-10 animate-float"
        style={{ animationDelay: "2s" }}
      ></div>

      <div className="w-full max-w-md animate-fade-up">
        <div className="mb-8 text-center">
          <div className="flex justify-center mb-4">
            <div className="h-12 w-12 rounded-xl bg-primary flex items-center justify-center shadow-md">
              <MessageSquare className="h-6 w-6 text-primary-foreground" />
            </div>
          </div>
          <h1 className="text-3xl font-bold">
            aiEmailAgent<span className="text-blue-600">.</span>
          </h1>
          <p className="text-muted-foreground mt-2">
            The AI-powered email assistant for sales teams
          </p>
        </div>

        <div className="glass rounded-xl p-8 shadow-lg border">
          <div className="space-y-2 mb-6">
            <h2 className="text-2xl font-bold">Reset Password</h2>
            <p className="text-muted-foreground text-sm">
              {isSuccess
                ? "Your password has been reset successfully"
                : "Enter your new password"}
            </p>
          </div>

          {!token && !isSuccess && (
            <div className="rounded-md bg-amber-50 p-4 mb-4">
              <p className="text-sm text-amber-800">
                Missing password reset token. Please request a new password
                reset link.
              </p>
              <div className="mt-4">
                <Button
                  onClick={() => navigate({ to: "/auth/forgot-password" })}
                  className="w-full"
                >
                  Request New Link
                </Button>
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive mb-4 animate-scale">
              {error}
            </div>
          )}

          {isSuccess ? (
            <div className="text-center space-y-6">
              <div className="rounded-md bg-green-50 p-4 mb-4 flex items-center">
                <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                <p className="text-sm text-green-800">
                  Your password has been reset successfully!
                </p>
              </div>
              <Button onClick={handleLogin} className="w-full">
                Sign In
              </Button>
            </div>
          ) : token ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label
                  htmlFor="password"
                  className="text-sm font-medium leading-none"
                >
                  New Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  disabled={resetPasswordMutation.isPending}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm shadow-sm transition-colors focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="confirmPassword"
                  className="text-sm font-medium leading-none"
                >
                  Confirm New Password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  disabled={resetPasswordMutation.isPending}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm shadow-sm transition-colors focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-primary text-primary-foreground py-2.5 mt-2 hover:shadow-md transition-all duration-200 rounded-lg text-sm font-medium"
                disabled={resetPasswordMutation.isPending}
              >
                {resetPasswordMutation.isPending ? (
                  <span className="flex items-center">
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Resetting Password...
                  </span>
                ) : (
                  "Reset Password"
                )}
              </Button>
            </form>
          ) : null}

          <div className="mt-6 text-center text-sm">
            <Link
              to="/auth/login"
              className="text-sm font-medium text-primary hover:underline"
              search={{ redirectTo: "/auth/reset-password" }}
            >
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
